// Fleet Health Management Component
// Provides comprehensive vehicle fleet monitoring with search, filtering, and detailed metrics
// Supports both grid and list view modes for optimal user experience
import { useState, useMemo } from 'react';
import { Search, MapPin, Truck, AlertTriangle, Activity, Shield, Grid, List, CheckCircle, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import VehicleCard from './VehicleCard';
import AddVehicleModal from './AddVehicleModal';

const FleetHealth = ({ data, onVehicleClick, onDataUpdate }) => {
  // Search and filter state management
  const [searchQuery, setSearchQuery] = useState('');
  const [alertFilter, setAlertFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [baseFilter, setBaseFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // Toggle between 'grid' and 'list' display modes
  
  // Modal state
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);

  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Handle vehicle addition
  const handleVehicleAdded = (newVehicle) => {
    // Trigger data refresh in parent component
    if (onDataUpdate) {
      onDataUpdate();
    }
  };

  // Flatten all vehicles from all countries and bases into a single array
  // Each vehicle is enriched with region and base information for filtering and display
  const allVehicles = useMemo(() => {
    const vehicles = [];
    data.countries.forEach((country) => {
      country.bases.forEach((base) => {
        base.vehicles.forEach((vehicle) => {
          vehicles.push({
            ...vehicle,
            region: country.name,
            regionId: country.id,
            baseName: base.name,
            baseId: base.id,
            baseLocation: base.location,
          });
        });
      });
    });
    
    console.log('🔄 FleetHealth: allVehicles recalculated', {
      totalVehicles: vehicles.length,
      sampleVehicle: vehicles[0] ? {
        id: vehicles[0].id,
        health: vehicles[0].health,
        components: vehicles[0].components
      } : null
    });
    
    return vehicles;
  }, [data]);

  // Calculate comprehensive fleet metrics for dashboard cards
  // Includes operational readiness calculations based on health thresholds and alert levels
  const metrics = useMemo(() => {
    const totalBases = data.countries.reduce((sum, country) => sum + country.bases.length, 0);
    const totalVehicles = allVehicles.length;
    const totalAlerts = allVehicles.filter(v => v.alertLevel !== 'low').length;
    const avgHealth = allVehicles.length > 0
      ? (allVehicles.reduce((sum, v) => sum + v.health, 0) / allVehicles.length).toFixed(1)
      : 0;

    // Mission readiness criteria: health >= 70% AND low alert level
    const readyVehicles = allVehicles.filter(v => v.health >= 70 && v.alertLevel === 'low');
    const missionReadyCount = readyVehicles.length;
    const missionReadyPercentage = allVehicles.length > 0
      ? ((missionReadyCount / allVehicles.length) * 100).toFixed(1)
      : 0;
    const operationalReadiness = allVehicles.length > 0
      ? ((readyVehicles.length / allVehicles.length) * 100).toFixed(1)
      : 0;

    return {
      totalBases,
      totalVehicles,
      totalAlerts,
      avgHealth,
      operationalReadiness,
      missionReadyCount,
      missionReadyPercentage,
    };
  }, [allVehicles, data]);

  // Get filtered bases based on selected region
  const availableBases = useMemo(() => {
    if (regionFilter === 'all') return [];
    const country = data.countries.find((c) => c.id === regionFilter);
    return country ? country.bases : [];
  }, [regionFilter, data]);

  // Get unique vehicle types from all vehicles
  const availableTypes = useMemo(() => {
    const types = new Set(allVehicles.map(v => v.type));
    return Array.from(types).sort();
  }, [allVehicles]);

  // Get unique vehicle models from all vehicles
  const availableModels = useMemo(() => {
    const models = new Set(allVehicles.map(v => `${v.brand} ${v.model}`));
    return Array.from(models).sort();
  }, [allVehicles]);



  // Apply comprehensive filtering based on search query and filter selections
  // Supports multi-field search across vehicle ID, model, brand, and base name
  const filteredVehicles = useMemo(() => {
    return allVehicles.filter((vehicle) => {
      // Multi-field search functionality
      const matchesSearch = searchQuery === '' ||
        vehicle.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.baseName.toLowerCase().includes(searchQuery.toLowerCase());

      // Filter by alert level (low, medium, high)
      const matchesAlert = alertFilter === 'all' || vehicle.alertLevel === alertFilter;

      // Filter by geographical region
      const matchesRegion = regionFilter === 'all' || vehicle.regionId === regionFilter;

      // Filter by specific operational base
      const matchesBase = baseFilter === 'all' || vehicle.baseId === baseFilter;

      // Filter by vehicle type
      const matchesType = typeFilter === 'all' || vehicle.type === typeFilter;

      // Filter by vehicle model
      const matchesModel = modelFilter === 'all' || `${vehicle.brand} ${vehicle.model}` === modelFilter;

      return matchesSearch && matchesAlert && matchesRegion && matchesBase && matchesType && matchesModel;
    });
  }, [allVehicles, searchQuery, alertFilter, regionFilter, baseFilter, typeFilter, modelFilter]);

  return (
    <div className="p-3 sm:p-4 md:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold ${themeClasses.textPrimary}`}>Fleet Health</h2>
          <p className={`${themeClasses.textMuted} mt-1 text-sm sm:text-base`}>Monitor and manage your entire vehicle fleet</p>
        </div>
        <button
          onClick={() => setShowAddVehicleModal(true)}
          className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
        >
          <Plus size={18} className="sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Add Fleet</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-4 sm:mb-6">
        <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-blue-500/50 transform transition-all duration-200`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Operational Bases</p>
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.totalBases}</h3>
              <p className={`${themeClasses.textMuted} text-sm`}>Across all regions</p>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
              <MapPin size={18} className="text-white" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-green-500/50 transform transition-all duration-200`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Total Vehicles</p>
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.totalVehicles}</h3>
              <p className={`${themeClasses.textMuted} text-sm`}>Fleet size</p>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-700 p-2 rounded-lg">
              <Truck size={18} className="text-white" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-emerald-500/50 transform transition-all duration-200`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Mission Ready</p>
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.missionReadyCount}</h3>
              <p className={`${themeClasses.textMuted} text-sm`}>{metrics.missionReadyPercentage}% of fleet</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-2 rounded-lg">
              <CheckCircle size={18} className="text-white" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-amber-500/50 transform transition-all duration-200`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Active Alerts</p>
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.totalAlerts}</h3>
              <p className={`${themeClasses.textMuted} text-sm`}>Maintenance issues</p>
            </div>
            <div className="bg-gradient-to-br from-amber-600 to-amber-700 p-2 rounded-lg">
              <AlertTriangle size={18} className="text-white" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-military-500/50 transform transition-all duration-200`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Avg Fleet Health</p>
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.avgHealth}%</h3>
              <p className={`${themeClasses.textMuted} text-sm`}>Readiness level</p>
            </div>
            <div className="bg-gradient-to-br from-military-600 to-military-700 p-2 rounded-lg">
              <Activity size={18} className="text-white" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-purple-500/50 transform transition-all duration-200`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Operational Readiness</p>
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.operationalReadiness}%</h3>
              <p className={`${themeClasses.textMuted} text-sm`}>Mission ready</p>
            </div>
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-2 rounded-lg">
              <Shield size={18} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-3 sm:p-4 mb-4 sm:mb-6`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
          {/* Search Bar */}
          <div className="lg:col-span-1">
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Search Vehicles</label>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textMuted}`} size={18} />
              <input
                type="text"
                placeholder="Search by ID, model, brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
              />
            </div>
          </div>

          {/* Vehicle Type Filter */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Vehicle Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            >
              <option value="all">All Types</option>
              {availableTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Vehicle Model Filter */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Vehicle Model</label>
            <select
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            >
              <option value="all">All Models</option>
              {availableModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Alert Level Filter */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Alert Level</label>
            <select
              value={alertFilter}
              onChange={(e) => setAlertFilter(e.target.value)}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            >
              <option value="all">All Alerts</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Region Filter */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Region</label>
            <select
              value={regionFilter}
              onChange={(e) => {
                setRegionFilter(e.target.value);
                setBaseFilter('all');
              }}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            >
              <option value="all">All Regions</option>
              {data.countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          {/* Base Filter */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Base</label>
            <select
              value={baseFilter}
              onChange={(e) => setBaseFilter(e.target.value)}
              disabled={regionFilter === 'all'}
              className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <option value="all">All Bases</option>
              {availableBases.map((base) => (
                <option key={base.id} value={base.id}>
                  {base.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchQuery('');
                setAlertFilter('all');
                setRegionFilter('all');
                setBaseFilter('all');
                setTypeFilter('all');
                setModelFilter('all');
              }}
              className={`w-full px-4 py-2 ${themeClasses.card} border ${themeClasses.border} rounded-lg ${themeClasses.textPrimary} hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors font-medium`}
            >
              Clear All Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Count and View Toggle */}
      <div className="mb-4 flex items-center justify-between">
        <p className={`${themeClasses.textMuted} text-sm`}>
          Showing <span className={`${themeClasses.textPrimary} font-semibold`}>{filteredVehicles.length}</span> of{' '}
          <span className={`${themeClasses.textPrimary} font-semibold`}>{allVehicles.length}</span> vehicles
        </p>

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'grid'
                ? 'bg-blue-600 text-white'
                : `${themeClasses.card} ${themeClasses.textMuted} hover:${themeClasses.textPrimary}`
            }`}
            title="Grid View"
          >
            <Grid size={20} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'list'
                ? 'bg-blue-600 text-white'
                : `${themeClasses.card} ${themeClasses.textMuted} hover:${themeClasses.textPrimary}`
            }`}
            title="List View"
          >
            <List size={20} />
          </button>
        </div>
      </div>

      {/* Vehicle Grid or List View */}
      <div className={viewMode === 'grid'
        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4'
        : 'space-y-3'
      }>
        {filteredVehicles.map((vehicle) => (
          <VehicleCard
            key={vehicle.id}
            vehicle={vehicle}
            onClick={() => onVehicleClick(vehicle)}
            viewMode={viewMode}
          />
        ))}
      </div>

      {/* No Results */}
      {filteredVehicles.length === 0 && (
        <div className="text-center py-12">
          <p className={`${themeClasses.textMuted} text-lg`}>No vehicles found matching your criteria</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setAlertFilter('all');
              setRegionFilter('all');
              setBaseFilter('all');
              setTypeFilter('all');
              setModelFilter('all');
            }}
            className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Add Vehicle Modal */}
      <AddVehicleModal
        isOpen={showAddVehicleModal}
        onClose={() => setShowAddVehicleModal(false)}
        onVehicleAdded={handleVehicleAdded}
      />
    </div>
  );
};

export default FleetHealth;
